/**
 * AccountInfoStep Component
 *
 * Step 1 of the vendor registration wizard.
 * Collects user account information: email, password, first name, surname.
 * Includes email availability check on blur.
 */

import { useState, useMemo, useCallback } from 'react';
import { Stack, Group } from '@/components/ui/layout';
import { Text, Button, Anchor } from '@/components/ui/core';
import { TextInput, PasswordInput } from '@/components/ui/inputs';
import { Alert } from '@/components/ui/feedback';
import { Progress } from '@mantine/core';
import { IconUser, IconMail, IconLock, IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { validators } from '@/utils/validation';
import { useVendorsServices } from '@actschurch/ccms-sdk/react';
import { useDebouncedCallback } from '@mantine/hooks';

export interface AccountInfoData {
  userEmail: string;
  password: string;
  confirmPassword: string;
  userFirstName: string;
  userSurname: string;
  userCellphone: string;
}

interface AccountInfoStepProps {
  data: AccountInfoData;
  onDataChange: (data: Partial<AccountInfoData>) => void;
  onNext: () => void;
}

export function AccountInfoStep({ data, onDataChange, onNext }: AccountInfoStepProps) {
  const vendorServices = useVendorsServices();
  const registrationService = vendorServices.vendorRegistration;

  // Error states
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [surnameError, setSurnameError] = useState('');
  const [cellphoneError, setCellphoneError] = useState('');

  // Email check states
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailExists, setEmailExists] = useState(false);

  // Password strength
  const passwordStrength = useMemo(() => {
    if (!data.password) return 0;
    return validators.passwordStrength(data.password);
  }, [data.password]);

  const strengthLabel = validators.passwordStrengthLabel(passwordStrength);
  const strengthColor = validators.passwordStrengthColor(passwordStrength);

  // Debounced email check
  const checkEmailAvailability = useDebouncedCallback(async (email: string) => {
    if (!email || validators.email(email) !== true) {
      setEmailExists(false);
      return;
    }

    setIsCheckingEmail(true);
    try {
      const exists = await registrationService.checkEmail(email);
      setEmailExists(exists);
    } catch {
      // Silent fail - don't block registration if check fails
      setEmailExists(false);
    } finally {
      setIsCheckingEmail(false);
    }
  }, 500);

  const handleEmailChange = useCallback((email: string) => {
    onDataChange({ userEmail: email });
    setEmailError('');
    checkEmailAvailability(email);
  }, [onDataChange, checkEmailAvailability]);

  const validate = (): boolean => {
    let isValid = true;

    // Validate first name
    const firstNameValidation = validators.required(data.userFirstName);
    if (firstNameValidation !== true) {
      setFirstNameError(firstNameValidation);
      isValid = false;
    } else {
      setFirstNameError('');
    }

    // Validate surname
    const surnameValidation = validators.required(data.userSurname);
    if (surnameValidation !== true) {
      setSurnameError(surnameValidation);
      isValid = false;
    } else {
      setSurnameError('');
    }

    // Validate email
    const emailValidation = validators.email(data.userEmail);
    if (emailValidation !== true) {
      setEmailError(emailValidation);
      isValid = false;
    } else {
      setEmailError('');
    }

    // Validate password
    const passwordValidation = validators.password(data.password);
    if (passwordValidation !== true) {
      setPasswordError(passwordValidation);
      isValid = false;
    } else {
      setPasswordError('');
    }

    // Validate confirm password
    const confirmPasswordValidation = validators.matchPassword(data.confirmPassword, data.password);
    if (confirmPasswordValidation !== true) {
      setConfirmPasswordError(confirmPasswordValidation);
      isValid = false;
    } else {
      setConfirmPasswordError('');
    }

    // Validate cellphone (optional)
    const cellphoneValidation = validators.optionalPhoneNumber(data.userCellphone);
    if (cellphoneValidation !== true) {
      setCellphoneError(cellphoneValidation);
      isValid = false;
    } else {
      setCellphoneError('');
    }

    return isValid;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <Stack gap="md">
      {/* Email exists info */}
      {emailExists && (
        <Alert
          icon={<IconInfoCircle size={16} />}
          title="Email Already Registered"
          color="blue"
        >
          <Text size="sm">
            This email is already registered. You can still register as a vendor and your existing
            account will be linked.{' '}
            <Anchor component={Link} to="/auth/login" size="sm" fw={500}>
              Login instead
            </Anchor>
          </Text>
        </Alert>
      )}

      <Group grow>
        <TextInput
          label="First Name"
          placeholder="John"
          leftSection={<IconUser size={16} />}
          value={data.userFirstName}
          onChange={(e) => {
            onDataChange({ userFirstName: e.target.value });
            setFirstNameError('');
          }}
          error={firstNameError}
          required
          size="md"
          radius="md"
        />

        <TextInput
          label="Surname"
          placeholder="Doe"
          leftSection={<IconUser size={16} />}
          value={data.userSurname}
          onChange={(e) => {
            onDataChange({ userSurname: e.target.value });
            setSurnameError('');
          }}
          error={surnameError}
          required
          size="md"
          radius="md"
        />
      </Group>

      <TextInput
        label="Email"
        placeholder="your@email.com"
        leftSection={<IconMail size={16} />}
        value={data.userEmail}
        onChange={(e) => handleEmailChange(e.target.value)}
        error={emailError}
        required
        size="md"
        radius="md"
        rightSection={isCheckingEmail ? <Text size="xs" c="dimmed">Checking...</Text> : null}
      />

      <TextInput
        label="Cellphone"
        placeholder="+27 12 345 6789"
        leftSection={<IconUser size={16} />}
        value={data.userCellphone}
        onChange={(e) => {
          onDataChange({ userCellphone: e.target.value });
          setCellphoneError('');
        }}
        error={cellphoneError}
        size="md"
        radius="md"
        description="Optional - used for account recovery"
      />

      <Stack gap="xs">
        <PasswordInput
          label="Password"
          placeholder="Create a strong password"
          leftSection={<IconLock size={16} />}
          value={data.password}
          onChange={(e) => {
            onDataChange({ password: e.target.value });
            setPasswordError('');
          }}
          error={passwordError}
          required
          size="md"
          radius="md"
        />
        {data.password && (
          <Stack gap={4}>
            <Progress value={passwordStrength} color={strengthColor} size="xs" />
            <Text size="xs" c={strengthColor}>
              Strength: {strengthLabel}
            </Text>
          </Stack>
        )}
      </Stack>

      <PasswordInput
        label="Confirm Password"
        placeholder="Confirm your password"
        leftSection={<IconLock size={16} />}
        value={data.confirmPassword}
        onChange={(e) => {
          onDataChange({ confirmPassword: e.target.value });
          setConfirmPasswordError('');
        }}
        error={confirmPasswordError}
        required
        size="md"
        radius="md"
      />

      <Group justify="flex-end" mt="md">
        <Button
          onClick={handleNext}
          size="md"
          radius="md"
          variant="gradient"
          gradient={{ from: 'indigo', to: 'violet' }}
        >
          Next: Business Info
        </Button>
      </Group>
    </Stack>
  );
}
